/* Split Column Slider
 *
 * One Swiper owns the media column (touch, autoplay, loop, arrows). The content
 * column is deliberately NOT a second Swiper — it is a stack of panels toggled
 * by `is-active` from Swiper's slide events, with the fade handled in CSS.
 *
 * Two linked Swipers (the pattern in split-screen-slideshow.js) also works, but
 * with `loop` + `controller` the two instances can disagree about realIndex
 * mid-transition, which shows up as the text briefly displaying the wrong slide.
 * A single source of truth removes that class of bug entirely, and lets the text
 * fade run on its own timing rather than being tied to the image's.
 *
 * Swiper 8.4.3 is loaded globally in layout/theme.liquid.
 */
(function () {
  'use strict';

  var SECTION_SELECTOR = '.split-column-slider';

  /* The <script> tag lives inside the section body, so a page with N instances
     of this section loads N identical script elements and the browser executes
     this IIFE N times — same-src scripts are NOT de-duplicated. A closure-scoped
     registry would therefore be empty on the 2nd run and every slider would get
     a second Swiper bound to the same element. The guard has to live on the DOM
     to survive re-execution. */
  var STARTED_ATTR = 'data-scs-initialised';
  var pendingLoadHook = false;

  function reducedMotion() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function t(root, key, fallback) {
    return root.dataset[key] || fallback;
  }

  function swiperOf(root) {
    var container = root.querySelector('.split-column-slider__media-swiper');
    return container ? container.swiper : null;
  }

  function buildEffectOptions(effect) {
    // 'zoom' is a slide transition plus a CSS scale on the active image, so as
    // far as Swiper is concerned it is still a slide.
    if (effect === 'fade') {
      return { effect: 'fade', fadeEffect: { crossFade: true } };
    }
    return { effect: 'slide' };
  }

  function setPanel(root, index) {
    var panels = root.querySelectorAll('.split-column-slider__panel');
    for (var i = 0; i < panels.length; i++) {
      var isActive = i === index;
      panels[i].classList.toggle('is-active', isActive);
      if (isActive) {
        panels[i].removeAttribute('aria-hidden');
      } else {
        panels[i].setAttribute('aria-hidden', 'true');
      }
    }
  }

  function announce(root, index, total) {
    var status = root.querySelector('.split-column-slider__status');
    if (!status) return;
    // While auto-rotating, the APG carousel pattern requires the live region to
    // be off — otherwise a screen reader is interrupted on every tick, forever.
    if (status.getAttribute('aria-live') === 'off') return;

    var panel = root.querySelector('.split-column-slider__panel[data-index="' + index + '"]');
    var heading = panel && panel.querySelector('.split-column-slider__heading');
    var label = heading ? heading.textContent.trim() : '';
    var template = t(root, 'i18nSlideOf', 'Slide {{ index }} of {{ total }}');
    var text = template
      .replace('{{ index }}', index + 1)
      .replace('{{ total }}', total);
    status.textContent = text + (label ? ': ' + label : '');
  }

  function setLiveRegion(root, autoplayRunning) {
    var status = root.querySelector('.split-column-slider__status');
    if (status) status.setAttribute('aria-live', autoplayRunning ? 'off' : 'polite');
  }

  function setCounter(root, index) {
    var current = root.querySelector('.split-column-slider__counter-current');
    if (current) current.textContent = String(index + 1);
  }

  /* Without loop the first/last arrows are dead ends. This uses aria-disabled,
     NOT the `disabled` property: disabling the button that currently holds
     focus makes the browser drop focus to <body>, and since the keyboard
     handler is scoped to the nav, the carousel then becomes unreachable by
     keyboard entirely. aria-disabled keeps it focusable; the click handler
     no-ops at the edge. */
  function setArrowState(root, swiper, loop) {
    if (loop) return;
    var prev = root.querySelector('.split-column-slider__arrow--prev');
    var next = root.querySelector('.split-column-slider__arrow--next');
    if (prev) prev.setAttribute('aria-disabled', swiper.isBeginning ? 'true' : 'false');
    if (next) next.setAttribute('aria-disabled', swiper.isEnd ? 'true' : 'false');
  }

  function hideNav(root) {
    var nav = root.querySelector('.split-column-slider__nav');
    var pagination = root.querySelector('.split-column-slider__pagination');
    // Degrade to a clean static hero rather than leaving live-looking controls
    // that silently do nothing.
    if (nav) nav.hidden = true;
    if (pagination) pagination.hidden = true;
  }

  function init(root) {
    if (root.hasAttribute(STARTED_ATTR)) return;

    var container = root.querySelector('.split-column-slider__media-swiper');
    if (!container) return;

    if (container.swiper) {
      root.setAttribute(STARTED_ATTR, '');
      return;
    }

    var count = parseInt(root.dataset.count, 10) || 0;
    if (count < 1) return;

    if (typeof window.Swiper !== 'function') {
      hideNav(root);
      if (!pendingLoadHook) {
        pendingLoadHook = true;
        window.addEventListener('load', function () {
          pendingLoadHook = false;
          if (typeof window.Swiper === 'function') initAll(document);
        });
      }
      return;
    }

    var loop = root.dataset.loop === 'true' && count > 1;
    var speed = parseFloat(root.dataset.speed) || 0.8;
    var delay = parseFloat(root.dataset.autoplayDelay) || 5;
    var pauseOnHover = root.dataset.pauseOnHover === 'true';
    // Never auto-rotate for someone who asked for reduced motion.
    var autoplayOn = root.dataset.autoplay === 'true' && count > 1 && !reducedMotion();

    var options = {
      speed: reducedMotion() ? 0 : Math.round(speed * 1000),
      loop: loop,
      allowTouchMove: count > 1,
      slidesPerView: 1,
      watchSlidesProgress: true,
      autoplay: autoplayOn
        ? { delay: Math.round(delay * 1000), disableOnInteraction: false }
        : false
    };

    var effectOptions = buildEffectOptions(root.dataset.mediaEffect);
    for (var key in effectOptions) {
      if (Object.prototype.hasOwnProperty.call(effectOptions, key)) {
        options[key] = effectOptions[key];
      }
    }

    /* Deliberately NOT using Swiper's `navigation` module. Its update() sets
       `el.disabled = true` on <button> elements at the ends of a non-looping
       slider — including the button that currently has focus, which makes the
       browser drop focus to <body> and, with the keyboard handler scoped to the
       nav, leaves the carousel unreachable. Wiring clicks by hand keeps the
       buttons focusable and lets aria-disabled carry the state instead. */

    var paginationEl = root.querySelector('.split-column-slider__pagination');
    if (paginationEl) {
      options.pagination = { el: paginationEl, clickable: true };
    }

    // realIndex is loop-aware; index is not. Always drive the panel from realIndex.
    function sync() {
      setPanel(root, this.realIndex);
      setCounter(root, this.realIndex);
      setArrowState(root, this, loop);
      announce(root, this.realIndex, count);
    }

    options.on = { init: sync, slideChange: sync };

    var swiper;
    try {
      swiper = new window.Swiper(container, options);
    } catch (err) {
      // Leaving STARTED_ATTR unset lets a later shopify:section:load retry.
      hideNav(root);
      return;
    }

    root.setAttribute(STARTED_ATTR, '');
    setLiveRegion(root, autoplayOn);

    var toggle = root.querySelector('.split-column-slider__toggle');
    if (toggle) {
      if (!autoplayOn) {
        /* The toggle is rendered from the merchant's `autoplay` setting alone,
           but autoplay is additionally suppressed under reduced motion. Left
           wired, the button would START autoplay for exactly the users who
           asked for none — and with Swiper's defaults (3s, pauseOnMouseEnter
           off), because `options.autoplay` was replaced with `false`. */
        toggle.hidden = true;
      } else {
        toggle.addEventListener('click', function () {
          var sw = swiperOf(root);
          if (!sw || !sw.autoplay) return;
          var playing = toggle.dataset.state === 'playing';
          var label = toggle.querySelector('.visually-hidden');
          if (playing) {
            sw.autoplay.stop();
            toggle.dataset.state = 'paused';
            if (label) label.textContent = t(root, 'i18nPlay', 'Play slideshow');
          } else {
            sw.autoplay.start();
            toggle.dataset.state = 'playing';
            if (label) label.textContent = t(root, 'i18nPause', 'Pause slideshow');
          }
          setLiveRegion(root, !playing);
        });
      }
    }

    /* Swiper's pauseOnMouseEnter binds to the swiper element only, so hovering
       the text column a shopper is actually reading would not pause. Bind to
       the whole section instead. */
    if (autoplayOn && pauseOnHover) {
      root.addEventListener('mouseenter', function () {
        var sw = swiperOf(root);
        if (sw && sw.autoplay && sw.autoplay.running) sw.autoplay.pause();
      });
      root.addEventListener('mouseleave', function () {
        var sw = swiperOf(root);
        if (!sw || !sw.autoplay || !sw.autoplay.paused) return;
        /* Swiper 8.4.3 exposes only {pause, run, start, stop} — there is NO
           `resume()`. Calling it threw a TypeError on every mouseout and left
           autoplay permanently paused (pause() leaves running === true, so a
           `running` guard does not catch it). Clearing `paused` and calling
           run() is what Swiper's own pauseOnMouseEnter handler does. */
        sw.autoplay.paused = false;
        sw.autoplay.run();
      });

      /* Hover pauses, but focus did not — a keyboard user reading the panel had
         it rotate out from under them, which also throws focus to <body> when
         the outgoing panel goes visibility:hidden. */
      root.addEventListener('focusin', function () {
        var sw = swiperOf(root);
        if (sw && sw.autoplay && sw.autoplay.running) sw.autoplay.pause();
      });
      root.addEventListener('focusout', function (event) {
        if (root.contains(event.relatedTarget)) return;
        var sw = swiperOf(root);
        if (!sw || !sw.autoplay || !sw.autoplay.paused) return;
        sw.autoplay.paused = false;
        sw.autoplay.run();
      });
    }

    /* Arrow keys only while focus is inside the nav. Binding to the section
       root hijacked the keys from focusable content in the panel. Handlers read
       the live instance rather than closing over it, so a destroy/re-init cycle
       cannot leave a stale closure throwing on a torn-down Swiper. */
    var arrows = root.querySelectorAll('.split-column-slider__arrow');
    for (var a = 0; a < arrows.length; a++) {
      arrows[a].addEventListener('click', function () {
        var sw = swiperOf(root);
        if (!sw) return;
        if (this.getAttribute('aria-disabled') === 'true') return;
        if (this.classList.contains('split-column-slider__arrow--prev')) sw.slidePrev();
        else sw.slideNext();
      });
    }

    var nav = root.querySelector('.split-column-slider__nav');
    if (nav) {
      nav.addEventListener('keydown', function (event) {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        var sw = swiperOf(root);
        if (!sw) return;
        event.preventDefault();
        if (event.key === 'ArrowLeft') sw.slidePrev();
        else sw.slideNext();
      });
    }
  }

  function destroy(root) {
    var container = root.querySelector('.split-column-slider__media-swiper');
    if (container && container.swiper && typeof container.swiper.destroy === 'function') {
      container.swiper.destroy(true, true);
    }
    root.removeAttribute(STARTED_ATTR);
  }

  function initAll(scope) {
    if (!scope) return;
    // On shopify:section:load the target may BE the slider or contain it.
    if (scope.matches && scope.matches(SECTION_SELECTOR)) init(scope);
    var roots = scope.querySelectorAll ? scope.querySelectorAll(SECTION_SELECTOR) : [];
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  /* Theme editor: sections are re-rendered on edit, so tear down and rebuild. */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var scope = event.target;
    if (!scope) return;
    if (scope.matches && scope.matches(SECTION_SELECTOR)) destroy(scope);
    var roots = scope.querySelectorAll ? scope.querySelectorAll(SECTION_SELECTOR) : [];
    for (var i = 0; i < roots.length; i++) destroy(roots[i]);
  });

  /* Jumping to a block in the editor should show that slide — and STAY there.
     Without stopping autoplay the preview rotates away from the slide the
     merchant just clicked in to edit. */
  document.addEventListener('shopify:block:select', function (event) {
    var root = event.target.closest(SECTION_SELECTOR);
    if (!root) return;
    var swiper = swiperOf(root);
    var index = parseInt(event.target.dataset.index, 10);
    if (!swiper || isNaN(index)) return;
    if (swiper.autoplay && swiper.autoplay.running) swiper.autoplay.stop();
    if (swiper.params.loop && typeof swiper.slideToLoop === 'function') {
      swiper.slideToLoop(index);
    } else {
      swiper.slideTo(index);
    }
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    var root = event.target.closest(SECTION_SELECTOR);
    if (!root || root.dataset.autoplay !== 'true' || reducedMotion()) return;
    var swiper = swiperOf(root);
    if (swiper && swiper.autoplay) swiper.autoplay.start();
  });
})();
